import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import AccountCard from '../AccountCard';

// Corporate Team dashboard: company-wide visibility (RLS returns every
// account for corporate users) with none of the admin tools.

const BUYING_STAGES = [
  { key: 'Discovery', color: '#6366f1', bg: '#eef2ff' },
  { key: 'Clinical Buy-in', color: '#0891b2', bg: '#ecfeff' },
  { key: 'Trial', color: '#d97706', bg: '#fffbeb' },
  { key: 'Value Analysis (VAC)', color: '#ea580c', bg: '#fff7ed' },
  { key: 'Closed - Won', color: '#059669', bg: '#ecfdf5' },
  { key: 'Closed - Lost', color: '#dc2626', bg: '#fef2f2' },
];

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);

const CorporateDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allSurgeons, setAllSurgeons] = useState([]);
  const [callCount7d, setCallCount7d] = useState(0);
  const [stageCounts, setStageCounts] = useState({});
  const [notStagedCount, setNotStagedCount] = useState(0);
  const [selectedStage, setSelectedStage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [latestCallMap, setLatestCallMap] = useState({});
  const [repMap, setRepMap] = useState({});
  const [cptMap, setCptMap] = useState({});
  const [cptPriceMap, setCptPriceMap] = useState({});

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const [surgeonsRes, callsCountRes] = await Promise.all([
          supabase.from('surgeons')
            .select('id, full_name, first_name, last_name, specialty, city, state, npi, buying_stage, forecast_close_date')
            .order('forecast_close_date', { ascending: true, nullsFirst: false }),
          supabase.from('call_logs')
            .select('id', { count: 'exact', head: true })
            .gte('call_date', weekAgo),
        ]);

        const surgeons = surgeonsRes.data || [];
        setAllSurgeons(surgeons);
        setCallCount7d(callsCountRes.count || 0);

        const counts = {};
        let noStage = 0;
        BUYING_STAGES.forEach(s => { counts[s.key] = 0; });
        surgeons.forEach(s => {
          if (s.buying_stage && counts[s.buying_stage] !== undefined) {
            counts[s.buying_stage]++;
          } else {
            noStage++;
          }
        });
        setStageCounts(counts);
        setNotStagedCount(noStage);

        const surgeonIds = surgeons.map(s => s.id);
        if (surgeonIds.length > 0) {
          const [callsRes, delegationsRes, cptDataRes, cptPricesRes] = await Promise.all([
            supabase.from('call_logs')
              .select('surgeon_id, call_date, summary, buying_stage_update, user_id, rep:users!call_logs_user_id_fkey(first_name, last_name)')
              .order('call_date', { ascending: false })
              .limit(500),
            supabase.from('account_delegations')
              .select('surgeon_id, user_id, rep:users!user_id(first_name, last_name)'),
            supabase.from('surgeon_cpt_data')
              .select('surgeon_id, cpt_code, cpt_description, annual_volume'),
            supabase.from('cpt_prices').select('cpt_code, average_price'),
          ]);

          const clMap = {};
          (callsRes.data || []).forEach(c => { if (!clMap[c.surgeon_id]) clMap[c.surgeon_id] = c; });
          setLatestCallMap(clMap);

          const rMap = {};
          (delegationsRes.data || []).forEach(d => { if (!rMap[d.surgeon_id]) rMap[d.surgeon_id] = d.rep; });
          setRepMap(rMap);

          const cMap = {};
          (cptDataRes.data || []).forEach(c => {
            if (!cMap[c.surgeon_id]) cMap[c.surgeon_id] = [];
            cMap[c.surgeon_id].push(c);
          });
          setCptMap(cMap);

          const pMap = {};
          (cptPricesRes.data || []).forEach(p => { if (p.average_price != null) pMap[p.cpt_code] = parseFloat(p.average_price); });
          setCptPriceMap(pMap);
        }
      } catch (err) {
        console.error('[CorporateDashboard] Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <span style={styles.loadingText}>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Company Overview */}
      <div style={styles.headerCard}>
        <h2 style={styles.pageTitle}>Company Overview</h2>
        <span style={styles.headerStats}>
          {allSurgeons.length} account{allSurgeons.length === 1 ? '' : 's'} · {callCount7d} call{callCount7d === 1 ? '' : 's'} (7d)
        </span>
      </div>

      {/* Deal Review */}
      <button onClick={() => navigate('/field-intel/deal-review')} style={styles.dealReviewBtn}>
        <span>Deal Review</span>
        <ArrowRightIcon />
      </button>

      {/* Pipeline */}
      <span style={styles.sectionTitle}>Pipeline</span>

      <button
        onClick={() => setSelectedStage(selectedStage === 'Not Staged' ? null : 'Not Staged')}
        style={{
          ...styles.notStagedBtn,
          backgroundColor: selectedStage === 'Not Staged' ? '#64748b' : '#f8fafc',
          color: selectedStage === 'Not Staged' ? '#ffffff' : '#64748b',
          border: selectedStage === 'Not Staged' ? '1px solid #64748b' : '1px solid #e2e8f0',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: '700' }}>{notStagedCount}</span>
        <span style={{ fontSize: '12px', fontWeight: '600' }}>Not Staged</span>
      </button>

      <div style={styles.stageGrid}>
        {BUYING_STAGES.map(stage => {
          const count = stageCounts[stage.key] || 0;
          const isSelected = selectedStage === stage.key;
          return (
            <button key={stage.key} onClick={() => setSelectedStage(isSelected ? null : stage.key)} style={{ ...styles.stageTile, backgroundColor: isSelected ? stage.color : stage.bg, border: `1px solid ${isSelected ? stage.color : stage.color + '20'}` }}>
              <span style={{ fontSize: '22px', fontWeight: '700', color: isSelected ? '#ffffff' : stage.color }}>{count}</span>
              <span style={{ fontSize: '11px', fontWeight: '600', color: isSelected ? '#ffffff' : '#475569', textAlign: 'center' }}>{stage.key}</span>
            </button>
          );
        })}
      </div>

      <input
        type="text"
        placeholder="Search accounts by name, city, NPI..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={styles.searchInput}
      />

      {(() => {
        const q = searchQuery.toLowerCase();
        const filtered = allSurgeons.filter(s => {
          if (selectedStage === 'Not Staged' && s.buying_stage) return false;
          if (selectedStage && selectedStage !== 'Not Staged' && s.buying_stage !== selectedStage) return false;
          if (q) {
            const name = (s.full_name || `${s.first_name || ''} ${s.last_name || ''}`).toLowerCase();
            const searchable = `${name} ${s.city || ''} ${s.state || ''} ${s.npi || ''} ${s.specialty || ''}`.toLowerCase();
            if (!searchable.includes(q)) return false;
          }
          return true;
        });

        if ((selectedStage || q) && filtered.length > 0) {
          return (
            <div style={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={styles.sectionTitle}>{selectedStage || 'Search Results'} ({filtered.length})</span>
                {selectedStage && <button onClick={() => setSelectedStage(null)} style={{ border: 'none', background: 'none', color: '#1e3a8a', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Clear</button>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {filtered.slice(0, 50).map(s => (
                  <AccountCard key={s.id} surgeon={s} latestCall={latestCallMap[s.id]} assignedRep={repMap[s.id] || null} cptData={cptMap[s.id]} cptPriceMap={cptPriceMap} searchQuery={searchQuery} />
                ))}
              </div>
            </div>
          );
        }
        if ((selectedStage || q) && filtered.length === 0) {
          return <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '14px' }}>No accounts found</div>;
        }
        return null;
      })()}

      <div style={{ height: '24px' }} />
    </div>
  );
};

const styles = {
  container: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 0',
    gap: '12px',
  },
  spinner: {
    width: '28px',
    height: '28px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#1e3a8a',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '14px',
    color: '#64748b',
  },
  headerCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0,
  },
  headerStats: {
    fontSize: '13px',
    color: '#64748b',
  },
  dealReviewBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '14px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e3a8a',
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  notStagedBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: '10px',
    padding: '10px',
    cursor: 'pointer',
  },
  stageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
  stageTile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    borderRadius: '10px',
    padding: '12px 6px',
    cursor: 'pointer',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
};

export default CorporateDashboard;
